import { createContext, useCallback, useContext, useState } from "react";

// Lets every independently-mounted <Toast> (each page/button owns its own
// toast state — there are 15+ call sites) find out how many OTHER toasts
// are alive at the same moment, and in what order, so two firing together
// (e.g. liking a post while also adding it to a folder) stack instead of
// drawing on top of each other at the exact same fixed position.
const ToastStackContext = createContext(null);

// Shared by every toast type (Toast.jsx, UndoToast.jsx) so their `bottom`
// values actually line up — Toast and UndoToast were each independently
// tuned to "clear the floating pill nav" and drifted to slightly different
// numbers (78 vs 90) despite being near-identical heights. Since the shared
// stack above just adds a flat 48px per slot regardless of which toast type
// is at which index, two different base values meant the gap between a
// Toast and an UndoToast landing in the same stack was actually 12px
// short of that, visibly overlapping.
export const TOAST_BASE_OFFSET = 78;
export const TOAST_BASE_OFFSET_WITH_COOKIE_BANNER = 150;

// Same reasoning as the base offsets above — Toast.jsx and UndoToast.jsx
// each independently hardcoded this same "48" for their per-slot stacking
// math. They happen to agree today, but nothing enforced that, and a future
// tweak to just one of them (a taller toast, say) would reintroduce the
// exact same silent-overlap bug in a different shape.
export const TOAST_STACK_SLOT_HEIGHT = 48;

export function ToastStackProvider({ children }) {
  const [ids, setIds] = useState([]);

  const register = useCallback(id => {
    setIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const unregister = useCallback(id => {
    setIds(prev => prev.filter(x => x !== id));
  }, []);

  return (
    <ToastStackContext.Provider value={{ ids, register, unregister }}>
      {children}
    </ToastStackContext.Provider>
  );
}

export function useToastStack() {
  return useContext(ToastStackContext);
}
